import type { IntegrationConnection } from './types';

/**
 * Journeys route name → embedded Journeys-app path template. The connection's
 * `models` (or `[dynamicSegment]`) fill the positional segments. Mirrors the
 * directory's `CONNECTOR_ROUTE_PATHS` and extends it with the routes only
 * connections reach (`integrations.forms.form_details`,
 * `integrations.reporting-webhooks.edit`, `integrations.data_out.type`).
 */
const ROUTE_TEMPLATES: Record<string, (models: (string | number)[]) => string> = {
  'integrations.customerio': () => 'integrations/customerio',
  'integrations.type': (m) => `integrations/${m[0] ?? ''}`,
  'integrations.data_out.type': (m) => `integrations/data_out/${m[0] ?? ''}`,
  'integrations.warehouse.type': (m) => `integrations/warehouse/${m[0] ?? ''}`,
  'integrations.warehouse.snowflake': () => 'integrations/warehouse/snowflake',
  'integrations.warehouse.bigquery': () => 'integrations/warehouse/bigquery',
  'integrations.warehouse.redshift': () => 'integrations/warehouse/redshift',
  'integrations.audiences.type.setup': (m) => `integrations/audiences/${m[0] ?? ''}/setup`,
  'integrations.audiences.type.settings': (m) => `integrations/audiences/${m[0] ?? ''}/settings`,
  'integrations.forms.type': (m) => `integrations/forms/${m[0] ?? ''}`,
  'integrations.forms.form_details': (m) => `integrations/forms/${m[0] ?? ''}/${m[1] ?? ''}`,
  'integrations.reporting-webhooks.new': () => 'integrations/reporting-webhooks/new',
  'integrations.reporting-webhooks.edit': (m) => `integrations/reporting-webhooks/${m[0] ?? ''}`,
  'integrations.segment_in': () => 'integrations/segment_in',
  'integrations.segment_out': () => 'integrations/segment_out',
  'integrations.mparticle_out': () => 'integrations/mparticle_out',
  'integrations.cdp_in': () => 'integrations/cdp_in',
  'integrations.cdp_out': () => 'integrations/cdp_out',
  'sql-imports': (m) => `integrations/sql-sync/${m[0] ?? ''}`,
  'actions.email.sending_domains': () => 'settings/actions/email/sending_domains',
  'actions.push': () => 'settings/actions/push',
  'actions.in_app': () => 'settings/actions/in_app',
  'actions.url_params': () => 'settings/actions/url_params',
  'actions.line': () => 'settings/actions/line',
  'actions.twilio': () => 'settings/actions/twilio',
  'actions.slack': () => 'settings/actions/slack',
  'actions.whatsapp': () => 'settings/actions/whatsapp',
};

/**
 * Resolve a Journeys connection's link target. An absolute `path` (the API
 * credentials hand-off, which is Hydra-owned) is used verbatim; a route name is
 * mapped to the embedded Journeys-app URL with its models filled in. Ported
 * from the Ember overview controller's `createJourneysHref`.
 */
export function createJourneysHref(
  connection: Pick<IntegrationConnection, 'path' | 'models' | 'dynamicSegment'>,
  workspaceId: string,
): string {
  const routeName = connection.path;
  if (routeName.startsWith('/')) {
    return routeName;
  }
  const models = connection.models ?? (connection.dynamicSegment != null ? [connection.dynamicSegment] : []);
  const template = ROUTE_TEMPLATES[routeName];
  const path = template != null ? template(models) : 'integrations';
  // oxlint-disable-next-line local-rules/enforce-generate-path -- embedded Journeys-app connector deep-link, not a Hydra router path
  return `/workspaces/${workspaceId}/journeys/${path}`;
}
